import type { OrganizationUserType } from '@mfw/common';

import { config as translationsConfig } from '@mfw/translations';
import { pickPrimary } from '@mfw/common';

import type {
  UiOrganizationCategoryTag,
  ApiOrganizationCategoryTag,
  UiOrganizationSalesContact,
  ApiOrganizationSalesContact,
  ApiOrganizationUpdate,
  UiOrganizationUpdate,
  ApiOrganization,
  UiOrganization,
} from '../../schemas/organizations.schema';
import type { Maybe } from '../../types/maybe.types';

import { mapUserAddressCountryFromApi, mapValue } from './users.mappers';
import { mergeDefaultSocialMedia } from './socialMedia.mappers';
import { mapTranslationsFromApi } from './translations.mappers';

export function createUpdateOrganization(
  args: UiOrganizationUpdate,
): ApiOrganizationUpdate {
  return {
    address: args?.address
      ? {
          city: mapValue(args.address?.city),
          country: mapValue(args.address?.country?.[0]?.value),
          isAddressPublic: mapValue(args.address?.isAddressPublic),
          street: mapValue(args.address?.street),
          zip: mapValue(args.address?.zip),
        }
      : undefined,
    // T12 — sent only when the form actually carries one; `omitDeepUndefined`
    // strips it otherwise, so an untouched save never rewrites the currency.
    defaultCurrency: mapValue(args?.defaultCurrency),
    // Foundation 1 §5.2 — same rule: sent only when the form actually
    // touched it, so an untouched save never rewrites the zone.
    timezone: mapValue(args?.timezone?.[0]?.value),
    // Task 11 — present only when the settings form saw the set change; an
    // empty array (reminders off) is a value, not an absence, and survives
    // `omitDeepUndefined`.
    membershipReminderDays: args?.membershipReminderDays,
    contact:
      args.contact?.phoneCountryCode && args.contact?.phoneNumber
        ? {
            isPhoneNumberPublic: mapValue(args.contact?.isPhoneNumberPublic),
            phoneCountryCode: mapValue(args.contact?.phoneCountryCode),
            phoneNumber: mapValue(args.contact?.phoneNumber),
          }
        : undefined,
    email: mapValue(args?.email),
    isPublic: mapValue(args?.isPublic),
    logoName: mapValue(args?.logoName),
    name: mapValue(args?.name),
    socialMedia: args?.socialMedia
      ? mapValue(mergeDefaultSocialMedia(args.socialMedia))
      : undefined,
  } satisfies ApiOrganizationUpdate;
}

function mapOrganizationTagsFromApi(
  tags: Maybe<Array<ApiOrganizationCategoryTag>>,
): Array<UiOrganizationCategoryTag> {
  return (tags ?? []).map((tag) => ({
    categoryId: tag.categoryId,
    id: tag.id,
    translations: mapTranslationsFromApi(tag.translations ?? []),
  }));
}

export function mapOrganizationFromApi(
  apiOrganization: Maybe<ApiOrganization>,
): Maybe<UiOrganization> {
  const primaryAddress = pickPrimary(apiOrganization?.addresses);
  const primaryContact = pickPrimary(apiOrganization?.contacts);
  return {
    id: apiOrganization?.id ?? '',
    address: primaryAddress
      ? {
          city: mapValue(primaryAddress?.city),
          country: mapUserAddressCountryFromApi(
            primaryAddress?.country?.countryCode,
          ),
          isAddressPublic: mapValue(primaryAddress?.isAddressPublic),
          state: mapValue(primaryAddress?.state),
          street: mapValue(primaryAddress?.street),
          zip: mapValue(primaryAddress?.zip),
        }
      : undefined,
    requireClientTermsOnConnect: mapValue(
      apiOrganization?.requireClientTermsOnConnect,
    ),
    trainersMayHaveOwnTerms: mapValue(apiOrganization?.trainersMayHaveOwnTerms),
    deskEnabled: mapValue(apiOrganization?.deskEnabled),
    lockerKeysEnabled: mapValue(apiOrganization?.lockerKeysEnabled),
    billingTaxId: mapValue(apiOrganization?.billingTaxId),
    billingLocked: mapValue(apiOrganization?.billingLocked),
    // Q3/Task 4: the boolean alone cannot tell "owes money" from "cancelled and
    // owes nothing", and this mapper builds the UI organization field by field —
    // a field it does not name is simply absent in the app, with nothing to fail.
    billingLockReason: mapValue(apiOrganization?.billingLockReason),
    about: mapValue(apiOrganization?.about),
    // The org's organization TYPE and its EXPERTISE — two relations, one tag
    // shape. `translations` normalized to the canonical Ui shape here (mapper
    // layer), never left raw for a component to reinterpret.
    categoryTags: mapOrganizationTagsFromApi(apiOrganization?.categoryTags),
    companyRegistrationNumber: mapValue(
      apiOrganization?.companyRegistrationNumber,
    ),
    contact: primaryContact
      ? {
          isPhoneNumberPublic:
            mapValue(primaryContact?.isPhoneNumberPublic) ?? false,
          phoneCountryCode:
            mapValue(primaryContact?.phoneCountryCode) ??
            translationsConfig.defaultCountryCode,
          phoneNumber: mapValue(primaryContact?.phoneNumber) ?? '',
        }
      : undefined,
    createdAt: mapValue(apiOrganization?.createdAt),
    // Ownership of a solo-trainer org lives here and nowhere else — that org
    // type has no OWNER-typed membership row (see `isOrganizationOwnerMembership`).
    createdBy: mapValue(apiOrganization?.createdBy),
    defaultCurrency: mapValue(apiOrganization?.defaultCurrency),
    timezone: mapValue(apiOrganization?.timezone),
    membershipReminderDays: mapValue(apiOrganization?.membershipReminderDays),
    email: mapValue(apiOrganization?.email),
    isEmailPublic: mapValue(apiOrganization?.isEmailPublic),
    expertiseTags: mapOrganizationTagsFromApi(apiOrganization?.expertiseTags),
    isPublic: mapValue(apiOrganization?.isPublic),
    logoName: mapValue(apiOrganization?.logoName),
    logoUrl: mapValue(apiOrganization?.logoUrl),
    name: mapValue(apiOrganization?.name),
    rebrandActive: mapValue(apiOrganization?.rebrandActive),
    theme: mapValue(apiOrganization?.theme),
    organizationUserType: mapValue(
      apiOrganization?.organizationUsers?.[0]?.type,
    ) as Maybe<OrganizationUserType>,
    permissions: mapValue(apiOrganization?.permissions),
    roleKey: mapValue(apiOrganization?.roleKey),
    socialMedia: apiOrganization?.socialMedia
      ? mergeDefaultSocialMedia(apiOrganization?.socialMedia)
      : undefined,
    status: mapValue(apiOrganization?.status),
    tenantId: mapValue(apiOrganization?.tenantId),
    type: mapValue(apiOrganization?.type),
    updatedAt: mapValue(apiOrganization?.updatedAt),
  };
}

export function mapOrganizationSalesContactFromApi(
  contact: ApiOrganizationSalesContact,
): UiOrganizationSalesContact {
  return {
    name: contact.name,
    email: contact.email ?? '',
    phone: contact.phone ?? '',
  };
}
